import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import { useCreateQuote } from '@/hooks/useDesk';
import { formatNadFromCents } from '@/lib/format';

type Line = {
  id: string;
  description: string;
  category: 'venue' | 'catering' | 'av' | 'stationery' | 'other';
  qty: number;
  unitCents: number;
};

const categories = ['venue', 'catering', 'av', 'stationery', 'other'] as const;

export function QuickQuotePage() {
  const navigate = useNavigate();
  const create = useCreateQuote();
  const [clientName, setClientName] = useState('');
  const [email, setEmail] = useState('');
  const [lines, setLines] = useState<Line[]>([
    {
      id: crypto.randomUUID(),
      description: 'Venue hire',
      category: 'venue',
      qty: 1,
      unitCents: 0,
    },
  ]);

  const total = lines.reduce((sum, l) => sum + l.qty * l.unitCents, 0);

  function updateLine(id: string, patch: Partial<Line>) {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const quote = await create.mutateAsync({
      company_name: clientName,
      contact_email: email || null,
      status: 'draft',
      lines: lines.map((l) => ({
        description: l.description,
        category: l.category,
        quantity: l.qty,
        unit_price_cents: l.unitCents,
      })),
    });
    navigate('/quotes');
    return quote;
  }

  return (
    <div>
      <PageHeader
        title="Quick quote"
        description="Phone-friendly path: client → lines → quote (then issue invoice from Quotes)."
      />
      <form
        onSubmit={onSubmit}
        className="space-y-5 rounded-xl border border-[var(--border)] bg-white/80 p-4 sm:p-6"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block font-medium">Client</span>
            <input
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Company or person"
              required
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Email (optional)</span>
            <input
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg text-dome-forest">Lines</h2>
            <button
              type="button"
              className="text-sm font-medium text-dome-leaf"
              onClick={() =>
                setLines((prev) => [
                  ...prev,
                  {
                    id: crypto.randomUUID(),
                    description: '',
                    category: 'other',
                    qty: 1,
                    unitCents: 0,
                  },
                ])
              }
            >
              Add line
            </button>
          </div>
          {lines.map((line) => (
            <div
              key={line.id}
              className="grid gap-2 rounded-lg border border-[var(--border)] p-3 sm:grid-cols-[1.4fr_0.8fr_0.5fr_0.8fr]"
            >
              <input
                className="rounded-md border border-[var(--border)] px-2 py-1.5 text-sm"
                placeholder="Description"
                value={line.description}
                onChange={(e) =>
                  updateLine(line.id, { description: e.target.value })
                }
                required
              />
              <select
                className="rounded-md border border-[var(--border)] px-2 py-1.5 text-sm"
                value={line.category}
                onChange={(e) =>
                  updateLine(line.id, {
                    category: e.target.value as Line['category'],
                  })
                }
              >
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <input
                className="rounded-md border border-[var(--border)] px-2 py-1.5 text-sm"
                type="number"
                min={1}
                value={line.qty}
                onChange={(e) =>
                  updateLine(line.id, { qty: Number(e.target.value) || 1 })
                }
              />
              <input
                className="rounded-md border border-[var(--border)] px-2 py-1.5 text-sm"
                type="number"
                min={0}
                step={0.01}
                placeholder="Unit NAD"
                value={line.unitCents ? (line.unitCents / 100).toString() : ''}
                onChange={(e) =>
                  updateLine(line.id, {
                    unitCents: Math.round(Number(e.target.value || 0) * 100),
                  })
                }
              />
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border)] pt-4">
          <p className="font-mono text-sm">
            Total:{' '}
            <span className="text-base font-semibold">
              {formatNadFromCents(total)}
            </span>
          </p>
          <button
            type="submit"
            disabled={create.isPending}
            className="rounded-lg bg-dome-brass px-4 py-2 font-medium text-dome-ink disabled:opacity-60"
          >
            {create.isPending ? 'Saving…' : 'Save draft quote'}
          </button>
        </div>
        {create.isError ? (
          <p className="text-sm text-dome-danger" role="alert">
            {(create.error as Error).message}
          </p>
        ) : null}
      </form>
    </div>
  );
}
